/**
 * Сервис для получения команд в реальном времени через Server-Sent Events (SSE)
 * Обеспечивает мгновенную доставку команд от админа к планшету (0-2 секунды)
 * Heartbeat остается как fallback на случай разрыва SSE соединения
 */
import { useDeviceStore } from '../store/deviceStore'
import { LogsRepo } from '../offline/logsRepo'
import { QueueProcessor, type Command } from '../offline/queueProcessor'

export class CommandsStreamService {
  private static reconnectTimeout: number | null = null
  private static reconnectAttempts = 0
  private static readonly RECONNECT_DELAY_BASE = 2000 // 2 секунды
  private static readonly RECONNECT_DELAY_MAX = 60000 // 60 секунд
  private static isIntentionallyClosed = false
  private static activeConnection: AbortController | null = null

  /**
   * Подключается к SSE stream команд
   */
  static connect(): () => void {
    const deviceStore = useDeviceStore.getState()
    
    if (!deviceStore.deviceId || !deviceStore.siteId || !deviceStore.deviceSecret) {
      console.warn('[CommandsStream] Cannot connect: device not registered')
      return () => {}
    }

    if (this.activeConnection) {
      console.log('[CommandsStream] Already connected')
      return () => this.disconnect()
    }

    this.isIntentionallyClosed = false
    this.startConnection()

    // Возвращаем функцию для отключения
    return () => this.disconnect()
  }

  /**
   * Начинает SSE соединение через fetch (поддерживает custom headers)
   */
  private static async startConnection(): Promise<void> {
    const deviceStore = useDeviceStore.getState()
    
    if (!deviceStore.deviceId || !deviceStore.deviceSecret) {
      console.warn('[CommandsStream] Cannot start: device not registered')
      return
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.recvi.ru'
      const url = `${apiBaseUrl}/api/v1/tablet/commands/stream`
      
      console.log('[CommandsStream] Connecting to SSE stream...')
      
      // Создаем AbortController для возможности отмены
      this.activeConnection = new AbortController()
      
      // Используем fetch с ReadableStream для поддержки custom headers
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'X-Device-ID': deviceStore.deviceId,
          'X-Device-Secret': deviceStore.deviceSecret,
        },
        signal: this.activeConnection.signal,
      })
      
      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`)
      }
      
      if (!response.body) {
        throw new Error('Response body is null')
      }
      
      console.log('[CommandsStream] ✅ Connected to command stream')
      this.reconnectAttempts = 0
      await LogsRepo.save('info', 'Connected to SSE command stream')
      
      // Читаем stream
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      
      while (!this.isIntentionallyClosed) {
        const { done, value } = await reader.read()
        
        if (done) {
          console.log('[CommandsStream] Stream ended')
          break
        }
        
        // Декодируем chunk
        buffer += decoder.decode(value, { stream: true })
        
        // Обрабатываем полные SSE сообщения (разделенные двойным \n)
        const messages = buffer.split('\n\n')
        buffer = messages.pop() || '' // Последнее неполное сообщение остается в буфере
        
        for (const message of messages) {
          if (!message.trim()) continue
          
          // Парсим SSE формат: "data: {...}"
          const dataMatch = message.match(/^data: (.+)$/m)
          if (!dataMatch) {
            // Это может быть keepalive (": keepalive")
            continue
          }
          
          try {
            const eventData = JSON.parse(dataMatch[1])
            
            // Проверяем тип события: обычная команда или системное событие
            if (eventData.type === 'pwa_update_available') {
              // Триггерим проверку нового SW и отправляем SKIP_WAITING если есть waiting SW.
              // Фактический reload произойдёт через controllerchange слушатель в main.tsx.
              console.log('[CommandsStream] 🚀 PWA update available via SSE, triggering SW check...')
              // Триггерим проверку SW — vite-plugin-pwa autoUpdate обработает остальное
              if ('serviceWorker' in navigator) {
                try {
                  const registration = await navigator.serviceWorker.getRegistration()
                  if (registration) {
                    await registration.update()
                  }
                } catch (err) {
                  console.error('[CommandsStream] SW update check failed:', err)
                }
              }
              continue // Не обрабатываем как обычную команду
            }
            
            // Обычная команда от админ-панели
            const command: Command = eventData
            console.log('[CommandsStream] 📦 Received command via SSE:', command.command_type)
            
            // Обрабатываем команду немедленно (в фоне)
            QueueProcessor.processCommand(command).catch((error) => {
              console.error('[CommandsStream] Error processing command:', error)
            })
            
          } catch (error) {
            console.error('[CommandsStream] Error parsing SSE event:', error)
          }
        }
      }
      
      // Соединение закрыто - пытаемся переподключиться
      if (!this.isIntentionallyClosed) {
        console.log('[CommandsStream] Connection closed unexpectedly, reconnecting...')
        this.scheduleReconnect()
      }
      
    } catch (error) {
      console.error('[CommandsStream] Failed to start connection:', error)
      
      // Пытаемся переподключиться если не было явного отключения
      if (!this.isIntentionallyClosed) {
        this.scheduleReconnect()
      }
    }
  }

  /**
   * Планирует переподключение с экспоненциальной задержкой
   * БЕСКОНЕЧНЫЕ попытки - SSE критичен для удаленного управления
   */
  private static scheduleReconnect(): void {
    if (this.isIntentionallyClosed) {
      console.log('[CommandsStream] Not reconnecting: intentionally closed')
      return
    }

    this.reconnectAttempts++
    
    // Экспоненциальная задержка: 2s, 4s, 8s, 16s, 32s, 60s (max)
    // После достижения max delay продолжаем пытаться каждые 60 секунд
    const delay = Math.min(
      this.RECONNECT_DELAY_BASE * Math.pow(2, Math.min(this.reconnectAttempts - 1, 5)),
      this.RECONNECT_DELAY_MAX
    )
    
    console.log(`[CommandsStream] Reconnecting in ${delay / 1000}s (attempt ${this.reconnectAttempts})`)
    
    // Очищаем предыдущий timeout если есть
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout)
    }
    
    this.reconnectTimeout = window.setTimeout(() => {
      console.log('[CommandsStream] Attempting reconnect... (attempt ${this.reconnectAttempts})')
      this.startConnection()
    }, delay)
  }

  /**
   * Отключается от SSE stream
   */
  static disconnect(): void {
    console.log('[CommandsStream] Disconnecting...')
    this.isIntentionallyClosed = true
    
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    
    // Отменяем активное соединение
    if (this.activeConnection) {
      this.activeConnection.abort()
      this.activeConnection = null
    }
    
    this.reconnectAttempts = 0
    console.log('[CommandsStream] ✅ Disconnected')
  }

  /**
   * Проверяет состояние соединения
   */
  static isConnected(): boolean {
    return !this.isIntentionallyClosed
  }
  
  /**
   * Получает количество попыток переподключения
   */
  static getReconnectAttempts(): number {
    return this.reconnectAttempts
  }
}
